class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    this.classBodyAlwaysMobileMenu = "mobile-nav";

    this.header = document.querySelector(".page-header");

    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );

    this.desktopMenuBar = document.querySelector(".wt-drawer--nav");

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";

    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";

    this.handleDesktopMenuClick = this.handleDesktopMenuClick.bind(this);

    this.keepHeaderVisible = this.keepHeaderVisible.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.disableStickyHeader();
  }

  calculateNavbarTopMargin() {
    const header = document.querySelector("#header");

    const navbar = document.querySelector("#wt-drawer-nav");

    if (!header || !navbar) return 0;

    const headerHeight = header.offsetHeight || 0;

    const navbarHeight = navbar.offsetHeight || 0;

    let marginTop = 0;

    if (navbarHeight > headerHeight) {
      marginTop = headerHeight - navbarHeight;
    } else {
      marginTop = Math.abs(navbarHeight - headerHeight);
    }

    navbar.style.setProperty("--top-margin", `${marginTop}px`);

    return marginTop;
  }

  get isDesktop() {
    return window.matchMedia("(min-width: 1200px)").matches;
  }

  get isHeaderWithDesktopNav() {
    return !document.body.classList.contains("mobile-nav");
  }

  get isMenuBarOpen() {
    return Boolean(
      this.header?.classList.contains(this.desktopHeaderWithMenuBarClass),
    );
  }

  handleHeaderAccessibility() {
    if (
      !this.header ||
      !this.desktopMenuBar ||
      !this.isDesktop ||
      !this.isHeaderWithDesktopNav
    ) {
      return;
    }

    const allMenuLinks =
      this.desktopMenuBar.querySelectorAll("a[data-menu-level]");

    const levelOneLinks = this.desktopMenuBar.querySelectorAll(
      "a[data-menu-level='1']",
    );

    const stickyEnabled = this.header.classList.contains(this.enabledClass);

    if (stickyEnabled && !this.isMenuBarOpen) {
      setTabindex(allMenuLinks, "-1");
    }

    if (stickyEnabled && this.isMenuBarOpen) {
      setTabindex(allMenuLinks, "0");
    }

    if (!stickyEnabled) {
      setTabindex(levelOneLinks, "0");

      if (this.desktopMenuTrigger) {
        setTabindex([this.desktopMenuTrigger], "-1");
      }
    } else if (this.desktopMenuTrigger) {
      setTabindex([this.desktopMenuTrigger], "0");
    }
  }

  showStickyHeader() {
    if (!this.header) return;

    this.header.classList.add(this.showClass);

    this.handleHeaderAccessibility();
    this.calculateNavbarTopMargin();
  }

  enableStickyHeaderState() {
    if (!this.header) return;

    /*
     * Add both classes together.
     * sticky-enabled activates compact sticky mode.
     * sticky-show prevents translateY(-100%).
     */
    this.header.classList.add(this.enabledClass, this.showClass);

    this.handleHeaderAccessibility();
    this.calculateNavbarTopMargin();
  }

  disableStickyHeaderState() {
    if (!this.header) return;

    /*
     * At the top of the page, remove compact mode,
     * but keep the header visible.
     */
    this.header.classList.remove(this.enabledClass);

    this.header.classList.add(this.showClass);

    this.handleHeaderAccessibility();
  }

  keepHeaderVisible() {
    /*
     * Never hide the header based on scroll direction.
     */
    this.showStickyHeader();
  }

  handleDesktopMenuClick(event) {
    event.preventDefault();

    this.desktopMenuBar?.classList.toggle("wt-drawer--nav-show");

    this.desktopMenuTrigger?.classList.toggle(
      "wt-header__sticky-menu-trigger--active",
    );

    this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);

    this.showStickyHeader();
    this.handleHeaderAccessibility();
  }

  enableStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    document.body.classList.add("page-header-sticky");

    /*
     * Keep the header visible from the beginning.
     */
    this.header.classList.add(this.showClass);

    this.calculateNavbarTopMargin();

    /*
     * Scroll listener only reinforces visibility.
     * It never removes sticky-show.
     */
    this.scrollHandler = this.keepHeaderVisible;

    window.addEventListener("scroll", this.scrollHandler, {
      passive: true,
    });

    this.desktopMenuTrigger?.addEventListener(
      "click",
      this.handleDesktopMenuClick,
    );

    const sentinel = document.querySelector(".sticky-header__threshold");

    if (sentinel) {
      this.stickyHeaderObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ isIntersecting }) => {
          if (isIntersecting) {
            this.disableStickyHeaderState();
          } else {
            this.enableStickyHeaderState();
          }
        });
      });

      this.stickyHeaderObserver.observe(sentinel);
    } else {
      /*
       * Safe fallback when the threshold element
       * is missing from a template.
       */
      this.enableStickyHeaderState();
    }
  }

  disableStickyHeader() {
    if (this.header) {
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
    }

    document.body.classList.remove("page-header-sticky");

    this.desktopMenuBar?.classList.remove("wt-drawer--nav-show");

    this.desktopMenuTrigger?.classList.remove(
      "wt-header__sticky-menu-trigger--active",
    );

    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);

      this.scrollHandler = null;
    }

    this.desktopMenuTrigger?.removeEventListener(
      "click",
      this.handleDesktopMenuClick,
    );

    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }
  }

  init() {
    if (this.isAlwaysMobileMenu) {
      document.body.classList.add(this.classBodyAlwaysMobileMenu);
    } else {
      document.body.classList.remove(this.classBodyAlwaysMobileMenu);
    }

    if (this.isSticky) {
      this.enableStickyHeader();
    } else {
      this.disableStickyHeader();
    }
  }
}

if (!customElements.get("page-header")) {
  customElements.define("page-header", PageHeaderSection);
}
